import { unwrapDescriptionString } from "./descriptionUtils";

/**
 * Document links referenced by an entity but not attached to it.
 *
 * Share notifications (Google Docs, Dropbox, OneDrive) carry the document as a
 * link rather than an attachment, and the AI formatter that produces
 * `formattedContent` keeps anchor text while dropping the href — so the only
 * place the URL survives is the raw HTML in `description`.
 */

/** Hosts whose links point at a real document rather than branding or unsubscribe chrome. */
const DOCUMENT_URL_PATTERNS = [
  { re: /^https?:\/\/docs\.google\.com\/(document|spreadsheets|presentation|forms)\//i, source: "Google Docs" },
  { re: /^https?:\/\/drive\.google\.com\/(file|open|drive)\//i, source: "Google Drive" },
  { re: /^https?:\/\/[\w.-]*dropbox\.com\/(s|scl)\//i, source: "Dropbox" },
  { re: /^https?:\/\/[\w.-]*sharepoint\.com\//i, source: "SharePoint" },
  { re: /^https?:\/\/[\w.-]*box\.com\/s\//i, source: "Box" },
  { re: /^https?:\/\/[\w.-]*\.atlassian\.net\/wiki\//i, source: "Confluence" },
  { re: /^https?:\/\/[\w.-]*notion\.so\//i, source: "Notion" },
];

function classifyUrl(url) {
  return DOCUMENT_URL_PATTERNS.find(({ re }) => re.test(url))?.source || null;
}

/**
 * Identity for de-duplication: share emails repeat the same document across the
 * title chip, the Open button and an Outlook-only VML fallback, each with
 * different tracking params, so compare on origin + path only.
 */
function dedupeKey(url) {
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`.replace(/\/(edit|view|preview)$/i, "");
  } catch {
    return url;
  }
}

function decodeEntities(value) {
  return (value || "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ");
}

function textOf(html) {
  return decodeEntities(String(html || "").replace(/<[^>]+>/g, " "))
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Extract document links from an entity's raw HTML description.
 *
 * @param {unknown} description Raw `description` field from getEntityContent.
 * @param {string} [fallbackLabel] Used when the anchor has no text of its own
 *   (image-only or button links) — typically the entity name.
 * @returns {Array<{ url: string, label: string, source: string }>}
 */
export function extractDocumentLinks(description, fallbackLabel = "") {
  const html = unwrapDescriptionString(description);
  if (!html || typeof html !== "string" || !html.includes("<a")) return [];

  const anchorRe = /<a\b[^>]*?href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;
  const seen = new Set();
  const links = [];
  let match;

  while ((match = anchorRe.exec(html)) !== null) {
    const url = decodeEntities(match[1]).trim();
    const source = classifyUrl(url);
    if (!source) continue;

    const key = dedupeKey(url);
    if (seen.has(key)) continue;
    seen.add(key);

    links.push({
      url,
      label: textOf(match[2]) || fallbackLabel.trim() || "Open document",
      source,
    });
  }

  return links;
}
